import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { DomainSuggestion, DomainVariation } from '~/types/domain'; // Import DomainVariation

// Key for storing favorites in localStorage
const FAVORITES_STORAGE_KEY = 'aidomains_favorites';

/* ---------- STORE SHAPE ---------- */
interface FavoritesState {
  favorites: DomainSuggestion[];
  // Removed isLoaded state
}

interface FavoritesActions {
  addFavorite: (domain: DomainSuggestion, originalQuery?: string) => void; // Ensure originalQuery is optional here
  removeFavorite: (baseName: string) => void;
  isFavorite: (baseName: string) => boolean;
  clearAllFavorites: () => void;
  toggleFavorite: (
    domainName: string,
    selectedTld: string,
    isAvailable: boolean,
    additionalTlds?: string[],
    originalQuery?: string // Added originalQuery to toggleFavorite
  ) => void;
  // Removed _setIsLoaded action
}

export type FavoritesStore = FavoritesState & FavoritesActions;

/* ---------- STORE IMPLEMENTATION ---------- */
export const useFavoritesStore = create<FavoritesStore>()(
  persist(
    immer(
      (set, get): FavoritesStore => ({
        favorites: [],
        // Removed isLoaded initialization
        // Removed _setIsLoaded implementation

        addFavorite: (domain, originalQuery) => {
          // Accept originalQuery
          set((state) => {
            // Avoid duplicates
            if (
              !state.favorites.some((fav) => fav.baseName === domain.baseName)
            ) {
              // Add domain with originalQuery
              state.favorites.push({ ...domain, originalQuery });
            }
          });
        },

        removeFavorite: (baseName) => {
          set((state) => {
            state.favorites = state.favorites.filter(
              (domain) => domain.baseName !== baseName
            );
          });
        },

        isFavorite: (baseName) => {
          // Use get() to access the current state inside an action if needed,
          // but here we can directly access the state from the closure.
          const { favorites } = get();
          return favorites.some((domain) => domain.baseName === baseName);
        },

        clearAllFavorites: () => {
          set({ favorites: [] });
        },

        toggleFavorite: (
          domainName,
          selectedTld,
          isAvailable,
          additionalTlds,
          originalQuery // Destructure originalQuery
        ) => {
          set((state) => {
            const favoriteIndex = state.favorites.findIndex(
              (fav) => fav.baseName === domainName
            );

            if (favoriteIndex !== -1) {
              // Domain is currently a favorite, remove it directly
              state.favorites.splice(favoriteIndex, 1);
            } else {
              // Domain is not a favorite, add it directly
              const variations: DomainVariation[] = [
                {
                  name: `${domainName}.${selectedTld}`,
                  available: isAvailable,
                  selected: true,
                  status: isAvailable ? 'available' : 'unavailable', // Use literal type
                  checking: false,
                  fromCache: false, // Assuming fresh add
                  // Add other potential fields from DomainVariation if necessary, e.g., error
                },
              ];

              if (additionalTlds && additionalTlds.length > 0) {
                additionalTlds.forEach((tld) => {
                  if (tld !== selectedTld) {
                    // Assuming availability is unknown or default for additional TLDs
                    // Ensure this object also matches DomainVariation
                    variations.push({
                      name: `${domainName}.${tld}`,
                      available: undefined, // This is acceptable per DomainVariation type
                      selected: false,
                      status: 'unchecked', // Use literal type
                      checking: false,
                      fromCache: false,
                      // Add other potential fields from DomainVariation if necessary
                    });
                  }
                });
              }

              const domainObj: DomainSuggestion = {
                baseName: domainName,
                variations,
                originalQuery, // Include originalQuery when adding via toggle
              };
              // Avoid duplicates check again, assuming toggle logic handles it
              state.favorites.push(domainObj);
            }
          });
          /* // Old logic using get()
          const { favorites, removeFavorite, addFavorite } = get(); // Get current state and actions

          const isCurrentlyFavorite = favorites.some(
            (domain) => domain.baseName === domainName
          );

          if (isCurrentlyFavorite) {
            removeFavorite(domainName);
          } else {
            // Explicitly type the variations array
            const variations: DomainVariation[] = [
              {
                name: `${domainName}.${selectedTld}`,
                available: isAvailable,
                selected: true,
                status: isAvailable ? 'available' : 'unavailable', // Use literal type
                checking: false,
                fromCache: false, // Assuming fresh add
                // Add other potential fields from DomainVariation if necessary, e.g., error
              },
            ];

            if (additionalTlds && additionalTlds.length > 0) {
              additionalTlds.forEach((tld) => {
                if (tld !== selectedTld) {
                  // Assuming availability is unknown or default for additional TLDs
                  // Ensure this object also matches DomainVariation
                  variations.push({
                    name: `${domainName}.${tld}`,
                    available: undefined, // This is acceptable per DomainVariation type
                    selected: false,
                    status: 'unchecked', // Use literal type
                    checking: false,
                    fromCache: false,
                    // Add other potential fields from DomainVariation if necessary
                  });
                }
              });
            }

            const domainObj: DomainSuggestion = {
              baseName: domainName,
              variations,
              originalQuery, // Also include here if uncommenting old logic
            };
            addFavorite(domainObj, originalQuery); // Pass originalQuery if uncommenting
          }
          */
        },
      })
    ),
    {
      name: FAVORITES_STORAGE_KEY, // Persistence key
      storage: createJSONStorage(() => localStorage), // Use localStorage
      // Only persist the favorites array
      partialize: (state) => ({ favorites: state.favorites }),
      // Removed onRehydrateStorage callback
    }
  )
);

// Hook to check hydration status using Zustand's built-in mechanism
import { useState, useEffect } from 'react';

export const useIsFavoritesLoaded = () => {
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    // Zustand's persist middleware has a `hasHydrated` method and allows subscription
    const unsubFinishHydration = useFavoritesStore.persist.onFinishHydration(
      () => {
        // console.log('[useIsFavoritesLoaded] Hydration finished.');
        setHydrated(true);
      }
    );

    // Check if already hydrated on mount (in case hydration finished before effect ran)
    if (useFavoritesStore.persist.hasHydrated()) {
      // console.log('[useIsFavoritesLoaded] Already hydrated on mount.');
      setHydrated(true);
    } else {
      // console.log('[useIsFavoritesLoaded] Waiting for hydration...');
    }

    return () => {
      unsubFinishHydration(); // Clean up subscription on unmount
    };
  }, []); // Empty dependency array ensures this runs only once on mount

  return hydrated;
};
